/**
 * Полоска состояния «N чатов ждут ответа» — внизу панели расширения и внизу списка «Чатов»; клик
 * открывает окно: какие чаты ждут дольше 5 минут, на каких наших аккаунтах и ГДЕ отвечать.
 *   ОТВЕТИТЬ ЗДЕСЬ — В «ЧАТАХ»            2    — официальный вход канала и мост; по праву `client_chats`
 *   из «Чатов» сейчас отвечают: Анурова У.
 *   WhatsApp «Wazzup»
 *     Галина                    ждёт 27 мин ↗
 *   ОТВЕТИТЬ В БРАУЗЕРЕ                   1    — чат нашего номера: ответить можно только в мессенджере
 *   MAX «Шоурум» · у вас открыт здесь
 *     Светлана                  ждёт 12 мин
 *     [ Открыть диалог ]  в «Чатах» ↗          — аккаунт этой вкладки: диалог открывается на месте
 *   ещё 2 у коллег ▸                           — аккаунт открыт у коллеги: в число не входят
 * В число идут чаты, чей аккаунт открыт у смотрящего или ни у кого (бесхозные), и общие «здесь».
 * `WaitingBar` — сама полоска (`variant`: токены панели или палитра `.tgc` «Чатов»), `WaitingList` —
 * содержимое окна (в «Чатах» его держит `Card`, в панели — `WaitingLayer` на всю панель).
 * Над ней, если очередь не пуста, — `CallbackBar` «N номеров ждут перезвона» (сверху «Обзвона»):
 * в «Чатах» клик открывает «Обзвон», в панели — `CallbackLayer` с «Звонить» (разметка 21.10.26).
 * Под ней — `LeadsBar` «Ждут дозвона: N новых лидов · M повторных» (очередь «Распределения»).
 * Страницу и роутер сюда не импортируем: файл собирается и в панель расширения.
 * Спека `docs/specs/2026-10-09-waiting-bar-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ExternalLink, Phone, PhoneMissed, PhoneOutgoing } from "lucide-react";
import { useEffect, useState, type ReactNode, type Ref } from "react";

import { PLAIN, accountName, channelOf, fmtAccountPhone } from "@/lib/clientChats";
import { btn } from "@/lib/panelBtn";
import { plural } from "@/lib/plural";
import { WAITING_POLL, loadWaiting, waited, type Callback, type CallbackRow, type Leads, type Waiting, type WaitingRow } from "@/lib/waiting";

/** Один запрос на панель и «Чаты»; после ответа клиенту и «Ответили» его сбрасывают (`["waiting"]`). */
export function useWaiting(enabled = true) {
  return useQuery({
    queryKey: ["waiting"],
    queryFn: loadWaiting,
    enabled,
    refetchInterval: WAITING_POLL,
    placeholderData: (prev) => prev,
  });
}
type Query = Pick<ReturnType<typeof useWaiting>, "data" | "error">;

/** «3 чата ждут ответа · самый давний 27 мин», «1 чат ждёт ответа · 12 мин». */
export function waitingText(w: Waiting): string {
  if (!w.count) return `Никто не ждёт дольше ${w.threshold} минут`;
  const head = `${w.count} ${plural(w.count, "чат ждёт", "чата ждут", "чатов ждут")} ответа`;
  return `${head} · ${w.count > 1 ? "самый давний " : ""}${waited(w.oldest)}`;
}

const BAR = {
  panel: {
    base: "h-7 border-t border-border px-3 text-[12.5px] leading-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent",
    none: "bg-bg-subtle text-fg-muted",
    warn: "bg-warn-bg text-fg",
    alert: "bg-err-bg text-err",
    off: "bg-bg-subtle text-fg-faint",
    dot: { warn: "bg-warn", alert: "bg-err" },
  },
  // Янтарный и красный — те, которыми колонка «Чатов» уже красит «Ждем клиента» и отказы.
  tgc: {
    base: "h-9 border-t border-[var(--tg-hover)] px-3 text-[13px] font-medium hover:bg-[var(--tg-hover)]",
    none: "text-[var(--tg-sec)]",
    warn: "bg-[#c98a00]/10 text-[#c98a00]",
    alert: "bg-[#e53935]/10 text-[#e53935]",
    off: "text-[var(--tg-sec)]",
    dot: { warn: "bg-current", alert: "bg-current" },
  },
} as const;

export function WaitingBar({
  variant,
  q,
  open,
  onToggle,
  className = "",
  ref,
}: {
  variant: keyof typeof BAR;
  q: Query;
  open: boolean;
  onToggle: () => void;
  className?: string;
  ref?: Ref<HTMLButtonElement>;
}) {
  const w = q.data;
  const look = BAR[variant];
  const level = w?.level ?? "none";
  const text = w ? waitingText(w) : q.error ? "Ждут ответа: нет связи с prokat" : "Ждут ответа: проверяю…";
  // Свежие данные не пришли — на экране прежние, причина — в подсказке.
  const title = w ? (q.error ? `${text}\nне обновляется: ${q.error.message}` : text) : q.error?.message;
  const Chevron = open ? ChevronDown : ChevronUp;
  return (
    <button
      ref={ref}
      type="button"
      aria-expanded={open}
      disabled={!w}
      title={title}
      onClick={onToggle}
      className={`flex w-full flex-none items-center gap-1.5 text-left ${look.base} ${w ? look[level] : look.off} ${className}`}
      data-annot="полоска «ждут ответа»"
    >
      {level !== "none" && <span className={`size-2 flex-none rounded-full ${look.dot[level]}`} />}
      <span className="min-w-0 flex-1 truncate">{text}</span>
      {w && <Chevron size={14} className="flex-none" aria-hidden />}
    </button>
  );
}

/** «2 номера ждут перезвона · самый давний 45 мин». */
export function callbackText(c: Callback): string {
  const head = `${c.count} ${plural(c.count, "номер ждёт", "номера ждут", "номеров ждут")} перезвона`;
  return `${head} · ${c.count > 1 ? "самый давний " : ""}${waited(c.oldest)}`;
}

/**
 * Вторая полоска — под «ждут ответа»: номера, ждущие перезвона после пропущенного входящего (сверху
 * «Обзвона», правило «Не перезвонили» «Звонков»). Пустой очереди полоски нет: спокойное «никто не
 * ждёт» уже говорит первая. «Чаты» — клик открывает «Обзвон», панель — окно со списком.
 */
export function CallbackBar({ variant, data, open, onToggle, className = "" }: { variant: keyof typeof BAR; data: Callback | null | undefined; open: boolean; onToggle: () => void; className?: string }) {
  if (!data?.count) return null;
  const look = BAR[variant];
  const level = data.level === "none" ? "warn" : data.level;
  const text = callbackText(data);
  const Chevron = open ? ChevronDown : ChevronUp;
  return (
    <button
      type="button"
      aria-expanded={open}
      title={`${text}\nдольше ${data.norm} минут — красным`}
      onClick={onToggle}
      className={`flex w-full flex-none items-center gap-1.5 text-left ${look.base} ${look[level]} ${className}`}
      data-annot="полоска «ждут перезвона»"
    >
      <PhoneMissed size={13} className="flex-none" aria-hidden />
      <span className="min-w-0 flex-1 truncate">{text}</span>
      <Chevron size={14} className="flex-none" aria-hidden />
    </button>
  );
}

/** «Ждут дозвона: 15 новых лидов · 14 повторных». */
export function leadsText(l: Leads): string {
  const parts = [
    l.new ? `${l.new} ${plural(l.new, "новый лид", "новых лида", "новых лидов")}` : "",
    l.redial ? `${l.redial} ${plural(l.redial, "повторный", "повторных", "повторных")}` : "",
  ];
  return `Ждут дозвона: ${parts.filter(Boolean).join(" · ")}`;
}

/**
 * Третья полоска — под «ждут ответа»: лиды, ждущие дозвона, — те же «не распределено» и «повторные
 * прозвоны», что на «Распределении» и во «Выделить» (разметка 21.10.26). Пустой очереди полоски нет.
 * «Чаты» — клик открывает «Обзвон» (там «Взять»), панель — только число.
 */
export function LeadsBar({ variant, data, onOpen, className = "" }: { variant: keyof typeof BAR; data: Leads | null | undefined; onOpen?: () => void; className?: string }) {
  if (!data || !(data.new + data.redial)) return null;
  const look = BAR[variant];
  const text = leadsText(data);
  const title = `${text}\nновые — не распределены, повторные — прозвоны в работе («Распределение»)`;
  const body = (
    <>
      <PhoneOutgoing size={13} className="flex-none" aria-hidden />
      <span className="min-w-0 flex-1 truncate">{text}</span>
    </>
  );
  const cls = `flex w-full flex-none items-center gap-1.5 text-left ${look.base} ${look.none} ${className}`;
  return onOpen ? (
    <button type="button" title={`${title}\nоткрыть «Обзвон» — взять себе`} onClick={onOpen} className={cls} data-annot="полоска «ждут дозвона»">
      {body}
    </button>
  ) : (
    <div title={title} className={cls} data-annot="полоска «ждут дозвона»">
      {body}
    </div>
  );
}

/** Окно перезвона в панели расширения — на всю панель, как «ждут ответа»: номер, линия, «Звонить». */
export function CallbackLayer({ data, onClose, base, onCall }: { data: Callback; onClose: () => void; base: string; onCall: (row: CallbackRow) => void }) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);
  return (
    <div role="dialog" aria-label="Номера, которые ждут перезвона" className="flex flex-1 flex-col text-[13px] text-fg" data-annot="окно «ждут перезвона»">
      <div className="sticky top-0 z-20 flex h-9 flex-none items-center gap-2 border-b border-border bg-bg px-2">
        <button type="button" className="inline-flex h-7 flex-none items-center gap-0.5 text-[12.5px] text-fg-muted hover:text-fg" onClick={onClose}>
          <ChevronLeft size={14} aria-hidden /> Назад
        </button>
        <span className="ml-auto truncate text-[13px] font-semibold">Ждут перезвона · {data.count}</span>
      </div>
      <div className="flex flex-col gap-1 p-3 text-[12.5px] leading-snug">
        {data.rows.length === 0 && <p className="text-fg-muted">Перезвона никто не ждёт</p>}
        {data.rows.map((r) => (
          <div key={r.phone} className="flex items-center gap-2 rounded-[var(--radius-ui)] px-1.5 py-1">
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate tabular-nums">{fmtAccountPhone(r.phone)}</span>
              <span className="truncate text-[11.5px] text-fg-muted">
                {[r.line, r.count > 1 ? `${r.count} ${plural(r.count, "пропуск", "пропуска", "пропусков")}` : "", r.attempts ? `звонили ${r.attempts} ${plural(r.attempts, "раз", "раза", "раз")}` : "ещё не звонили"]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </div>
            <span className={`flex-none tabular-nums ${r.minutes >= data.norm ? "text-err" : "text-fg-muted"}`} title={`первый пропуск в ${r.from.slice(11, 16)}`}>
              ждёт {waited(r.minutes)}
            </span>
            <a className={btn.quiet} href={`sip:+${r.phone}`} title="Звонок наберёт софтфон (MicroSIP)" onClick={() => onCall(r)}>
              <Phone size={12} aria-hidden /> Звонить
            </a>
          </div>
        ))}
        <a className={`${smallLink} mt-1 self-start`} href={`${base}/chats?view=dial`} target="_blank" rel="noreferrer">
          «Обзвон» в «Чатах» — переписка и звонки номера <ExternalLink size={11} aria-hidden />
        </a>
      </div>
    </div>
  );
}

/** Панель расширения: адрес кабинета (ссылки — в новую вкладку) и аккаунт этой вкладки площадки. */
export type WaitingPanel = {
  base: string;
  /** Площадка вкладки и id вошедшего на ней аккаунта; в Bitrix24 аккаунта нет — пусто. */
  channel: string;
  account: string;
  /** «Открыть диалог» — переключить диалог вкладки на месте. `busy` — чат, который открываем. */
  onDialog: (row: WaitingRow) => void;
  busy: number | null;
  error: string;
  /** Переход по ссылке строки — в журнал вкладки. */
  onGo: (row: WaitingRow, how: "here" | "cabinet") => void;
};

const smallLink = "inline-flex h-6 items-center gap-0.5 whitespace-nowrap text-accent-text hover:underline";
const blockTitle = "flex items-baseline justify-between gap-2 text-[10.5px] uppercase tracking-wide text-fg-faint";
// Подпись аккаунта говорит, где он открыт и что делать, — переносим, не обрезаем.
const groupTitle = "text-[12px] leading-snug text-fg-muted";

/** «Анурова Ульяна» → «Анурова У.». */
function short(name: string): string {
  const [first, second] = name.trim().split(/\s+/);
  return second ? `${first} ${second[0]}.` : (first ?? "");
}

const clientName = (r: WaitingRow) => r.name || (r.phone ? fmtAccountPhone(r.phone) : "без имени");

/** «WhatsApp «Шоурум»», «Telegram «Прием 9» · через мост», «Telegram · бот», «Авито», «Instagram «ВРН»». */
function accountTitle(r: WaitingRow): string {
  const ch = channelOf(r.channel);
  const name = r.channel === "instagram" && r.account.name ? `«${r.account.name}»` : accountName(r.account);
  const title = name
    ? `${ch.label} ${name}`
    : r.account.web
      ? `${ch.label} · аккаунт ${r.account_id}`
      : [ch.label, PLAIN[r.channel]].filter(Boolean).join(" · ");
  return r.place === "bridge" ? `${title} · через мост` : title;
}

/** Аккаунт чата — тот, что вошёл на этой вкладке площадки: диалог можно открыть на месте. */
const onThisTab = (r: WaitingRow, panel?: WaitingPanel) =>
  !!panel && !!r.account_id && panel.channel === r.channel && panel.account === r.account_id;

/** Где аккаунт чата открыт сейчас: по этому сотрудник понимает, куда идти отвечать. */
function openWhere(r: WaitingRow, panel?: WaitingPanel): string {
  if (onThisTab(r, panel)) return "у вас открыт здесь";
  if (r.mine) return `у вас открыт в ${channelOf(r.channel).label}`;
  if (r.owner) return `открыт у: ${r.owner.name || `#${r.owner.id}`}`;
  // В «Чатах» строки действий нет — что делать, говорит сама подпись.
  return panel ? "ни у кого не открыт" : "ни у кого не открыт — откройте аккаунт в своём браузере";
}

/** Чаты одного нашего аккаунта подряд; группы — в порядке самого давнего чата. */
function groups(rows: WaitingRow[]): WaitingRow[][] {
  const by = new Map<string, WaitingRow[]>();
  for (const r of rows) {
    const key = [r.place, r.channel, r.account_id ?? "", r.account.name, r.account.phone].join("|");
    by.set(key, [...(by.get(key) ?? []), r]);
  }
  return [...by.values()];
}

function Wait({ row, alert }: { row: WaitingRow; alert: number }) {
  return (
    <span className={`flex-none tabular-nums ${row.minutes >= alert ? "text-err" : "text-fg-muted"}`} title={`ждёт с ${row.since.slice(11, 16)}`}>
      ждёт {waited(row.minutes)}
    </span>
  );
}

function Row({ row, alert, browser, onChat, panel }: { row: WaitingRow; alert: number; browser: boolean; onChat?: ((row: WaitingRow) => void) | undefined; panel?: WaitingPanel | undefined }) {
  const line = "flex h-8 w-full items-center gap-2 rounded-[var(--radius-ui)] px-1.5 text-left text-[12.5px]";
  const body = (
    <>
      <span className="min-w-0 flex-1 truncate">{clientName(row)}</span>
      <Wait row={row} alert={alert} />
    </>
  );
  // «Чаты»: строка целиком — переход к чату внутри страницы.
  if (onChat)
    return (
      <button type="button" className={`${line} hover:bg-bg-hover`} onClick={() => onChat(row)} title="Открыть чат">
        {body}
      </button>
    );
  const href = `${panel?.base ?? ""}/chats/${row.id}`;
  // Панель, «здесь»: чат в кабинете, в новой вкладке.
  if (!browser)
    return (
      <a className={`${line} hover:bg-bg-hover`} href={href} target="_blank" rel="noreferrer" title="Открыть чат в «Чатах» — ответить оттуда" onClick={() => panel?.onGo(row, "here")}>
        {body}
        <ExternalLink size={12} className="flex-none text-fg-faint" aria-hidden />
      </a>
    );
  // Панель, «в браузере»: аккаунт этой вкладки — диалог на месте; иначе — чат в кабинете (только
  // чтение): диалог чужого аккаунта из этого браузера не открыть.
  const here = onThisTab(row, panel);
  // WhatsApp id чата в адрес не ставит — диалог открывается по номеру клиента.
  const can = here && (row.channel !== "whatsapp" || !!row.phone);
  return (
    <div className="flex flex-col">
      <div className={line}>{body}</div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-1.5 pb-1 text-[11.5px] text-fg-muted">
        {can && (
          <button type="button" className={btn.quiet} disabled={panel?.busy !== null} onClick={() => panel?.onDialog(row)}>
            {panel?.busy === row.id ? "Открываю…" : "Открыть диалог"}
          </button>
        )}
        <a className={smallLink} href={href} target="_blank" rel="noreferrer" title="Переписка в «Чатах» — только чтение" onClick={() => panel?.onGo(row, "cabinet")}>
          в «Чатах» <ExternalLink size={11} aria-hidden />
        </a>
        {!here && !row.mine && !row.owner && <span>· откройте аккаунт в своём браузере</span>}
      </div>
    </div>
  );
}

function Block({ title, count, note, children }: { title: string; count: number; note?: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-1.5">
      <h3 className={blockTitle}>
        <span>{title}</span>
        <span className="tabular-nums">{count}</span>
      </h3>
      {note && <p className={groupTitle}>{note}</p>}
      {children}
    </section>
  );
}

function Groups({ rows, alert, browser, onChat, panel }: { rows: WaitingRow[]; alert: number; browser: boolean; onChat?: ((row: WaitingRow) => void) | undefined; panel?: WaitingPanel | undefined }) {
  return groups(rows).map((g) => {
    const first = g[0]!;
    const title = browser ? `${accountTitle(first)} · ${openWhere(first, panel)}` : accountTitle(first);
    return (
      <div key={`${first.id}`} className="flex flex-col">
        <p className={groupTitle} title={[title, accountName(first.account, true)].filter(Boolean).join("\n")}>
          {title}
        </p>
        {g.map((r) => (
          <Row key={r.id} row={r} alert={alert} browser={browser} onChat={onChat} panel={panel} />
        ))}
      </div>
    );
  });
}

/**
 * Содержимое окна. `onChat` — «Чаты»: клик по строке открывает чат на этой же странице; `panel` —
 * панель расширения: ссылки в кабинет и «Открыть диалог» для аккаунта вкладки.
 */
export function WaitingList({ data, onChat, panel }: { data: Waiting; onChat?: (row: WaitingRow) => void; panel?: WaitingPanel }) {
  const [more, setMore] = useState(false);
  const who = data.here_online.users.map((u) => short(u.name || `#${u.id}`)).join(", ");
  const More = more ? ChevronDown : ChevronRight;
  return (
    <div className="flex flex-col gap-3 text-[12.5px] leading-snug text-fg" data-annot="окно «ждут ответа»">
      {data.count === 0 && (
        <p className="text-fg-muted">{data.can_here ? `Никто не ждёт дольше ${data.threshold} минут` : "У вас нет чатов, ждущих ответа"}</p>
      )}
      {data.here.length > 0 && (
        <Block
          title="Ответить здесь — в «Чатах»"
          count={data.here.length}
          note={who ? `из «Чатов» сейчас ${data.here_online.users.length > 1 ? "отвечают" : "отвечает"}: ${who}` : "из «Чатов» сейчас никто не отвечает"}
        >
          <Groups rows={data.here} alert={data.alert} browser={false} onChat={onChat} panel={panel} />
        </Block>
      )}
      {data.browser.length > 0 && (
        <Block title="Ответить в браузере" count={data.browser.length}>
          <Groups rows={data.browser} alert={data.alert} browser onChat={onChat} panel={panel} />
        </Block>
      )}
      {panel?.error && <p role="alert" className="rounded-[var(--radius-ui)] bg-err-bg px-2 py-1.5 text-err">{panel.error}</p>}
      {data.colleagues.length > 0 && (
        <section className="flex flex-col gap-1.5 border-t border-border pt-2">
          <button type="button" aria-expanded={more} className="inline-flex h-6 items-center gap-1 self-start text-fg-muted hover:text-fg" onClick={() => setMore(!more)}>
            ещё {data.colleagues.length} у коллег <More size={13} aria-hidden />
          </button>
          {more && <Groups rows={data.colleagues} alert={data.alert} browser onChat={onChat} panel={panel} />}
        </section>
      )}
    </div>
  );
}

/** Окно в панели расширения — на всю панель, как «Время записи»: в 320 px поповеру негде встать. */
export function WaitingLayer({ q, onClose, panel }: { q: Query; onClose: () => void; panel: WaitingPanel }) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);
  const w = q.data;
  return (
    <div role="dialog" aria-label="Чаты, которые ждут ответа" className="flex flex-1 flex-col text-[13px] text-fg">
      <div className="sticky top-0 z-20 flex h-9 flex-none items-center gap-2 border-b border-border bg-bg px-2">
        <button type="button" className="inline-flex h-7 flex-none items-center gap-0.5 text-[12.5px] text-fg-muted hover:text-fg" onClick={onClose}>
          <ChevronLeft size={14} aria-hidden /> Назад
        </button>
        <span className="ml-auto truncate text-[13px] font-semibold">
          Ждут ответа дольше {w?.threshold ?? 5} мин{w ? ` · ${w.count}` : ""}
        </span>
      </div>
      <div className="p-3">
        {w ? <WaitingList data={w} panel={panel} /> : <p className="text-[12.5px] text-fg-muted">Нет связи с prokat: {q.error?.message ?? "жду ответа"}</p>}
      </div>
    </div>
  );
}
